import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function AutoRestart() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [servers, setServers] = useState([]);
  const [form, setForm] = useState({ server_name: "", interval_hours: 6, channel_id: "" });
  const load = () => api.get("/admin/autorestart/list").then(({ data }) => setList(data.schedules || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setForm((f) => ({ ...f, server_name: list[0].name || list[0].slug }));
    }).catch(() => {});
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/autorestart/set", {
        server_name: form.server_name,
        interval_hours: parseInt(form.interval_hours, 10),
        channel_id: parseInt(form.channel_id, 10),
      });
      toast.success("Schedule saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (sn, enabled) => {
    try { await api.put(`/admin/autorestart/${encodeURIComponent(sn)}/toggle?enabled=${!enabled}`); toast.success(`${enabled ? "Disabled" : "Enabled"}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (sn) => {
    if (!window.confirm(`Remove auto-restart for ${sn}?`)) return;
    try { await api.delete(`/admin/autorestart/${encodeURIComponent(sn)}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4 fade-up" data-testid="autorestart-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Auto-Restart Schedules</h3>
        <p className="text-xs text-[var(--muted)]">
          The bot's scheduler reads from this table and restarts each server on the configured interval. Announcement goes to the chosen Discord channel.
        </p>
      </div>
      <form onSubmit={save} className="glass-card p-5 space-y-3" data-testid="autorestart-form">
        <h3 className="label">Add / Update Schedule</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Server">
            <select className="input" value={form.server_name} onChange={(e) => setForm({ ...form, server_name: e.target.value })} data-testid="ar-server">
              {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
            </select>
          </Field>
          <Field label="Interval (hours)"><input className="input" type="number" min="1" max="168" required value={form.interval_hours} onChange={(e) => setForm({ ...form, interval_hours: e.target.value })} data-testid="ar-interval" /></Field>
          <Field label="Announcement Channel ID"><input className="input font-mono text-xs" required value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="ar-channel" /></Field>
        </div>
        <button className="btn" data-testid="ar-submit">Save</button>
      </form>
      <div className="glass-card p-5" data-testid="autorestart-list">
        <h3 className="label mb-3">Schedules ({list.length})</h3>
        {list.length === 0 && <p className="text-sm text-[var(--muted)]">No auto-restart schedules.</p>}
        <ul className="divide-y divide-white/5">
          {list.map((s) => (
            <li key={s.server_name} className="flex items-center justify-between py-3 text-sm" data-testid={`ar-row-${s.server_name}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{s.server_name}</div>
                <div className="text-[10px] text-[var(--muted)]">
                  Every {s.interval_hours}h · channel {s.channel_id}
                  {s.last_restart && ` · last ${s.last_restart}`}
                  {!s.enabled && <span className="badge text-[var(--muted)] border-white/10 ml-1">disabled</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => toggle(s.server_name, !!s.enabled)} className={`btn btn-sm ${s.enabled ? "btn-danger" : ""}`} data-testid={`ar-toggle-${s.server_name}`}>
                  {s.enabled ? "Disable" : "Enable"}
                </button>
                <button onClick={() => remove(s.server_name)} className="btn btn-sm btn-danger" data-testid={`ar-remove-${s.server_name}`}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
